import { FC, KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAiAgentMessages } from '@/entities/ai-agent-chat';
import { useAiAgentStreaming } from '@/features/ai-agent-chat';
import { useCopilot } from '@/features/copilot';
import { CopilotChatPresenter } from './CopilotChatPresenter';

type Props = {
  className?: string;
};

export const CopilotChat: FC<Props> = ({ className }) => {
  const { activeChatId, isCreatingChat, showChatList, createNewChat } = useCopilot();

  const [inputValue, setInputValue] = useState('');

  const { data: messagesData } = useAiAgentMessages(activeChatId ?? 0);

  const { isGenerating, streamingTokens, pendingUserMessages, hasReplyFailed, sendChatMessage } = useAiAgentStreaming(
    activeChatId ?? 0,
  );

  const isInputDisabled = isGenerating || isCreatingChat;

  const messages = useMemo(
    () => [...(messagesData?.items || []), ...pendingUserMessages],
    [messagesData?.items, pendingUserMessages],
  );

  const pendingMessageRef = useRef<string | null>(null);

  const handleSend = useCallback(async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || isInputDisabled) return;

    setInputValue('');

    if (!activeChatId) {
      pendingMessageRef.current = trimmed;
      await createNewChat();
    } else {
      sendChatMessage(trimmed);
    }
  }, [inputValue, isInputDisabled, activeChatId, createNewChat, sendChatMessage]);

  useEffect(() => {
    if (activeChatId && pendingMessageRef.current) {
      const msg = pendingMessageRef.current;
      pendingMessageRef.current = null;
      sendChatMessage(msg);
    }
  }, [activeChatId, sendChatMessage]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  return (
    <CopilotChatPresenter
      className={className}
      messages={messages}
      isGenerating={isGenerating}
      hasReplyFailed={hasReplyFailed}
      isCreating={isCreatingChat}
      isInputDisabled={isInputDisabled}
      streamingTokens={streamingTokens}
      inputValue={inputValue}
      onInputChange={setInputValue}
      onKeyDown={handleKeyDown}
      onSend={handleSend}
      onBackToList={showChatList}
    />
  );
};
